import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { setAppLanguage } from '@/i18n';
import type { LanguageCode } from '@/i18n';
import { useStore } from '@/store/useStore';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { motion, AnimatePresence } from 'framer-motion';
import { BalanceBar } from '@/components/ui/BalanceBar';
import {
  ChevronRight,
  UserCheck,
  Pause,
  Percent,
  X,
  Crown,
  BadgeCheck,
  AtSign,
  Sparkles,
  ShoppingBag,
  Handshake,
  Tag,
  Gavel,
} from 'lucide-react';
import { v2Api } from '@/utils/v2Api';
import { CustomEmojiPlayer } from '@/components/ui/CustomEmojiPlayer';

const MARKETING_CHANNEL = 'https://t.me/KiteMarketplace';

const openTelegram = (url: string) => {
  // @ts-ignore
  const wa = window.Telegram?.WebApp;
  if (wa?.openTelegramLink) {
    wa.openTelegramLink(url);
  } else {
    window.open(url, '_blank', '_blank');
  }
};

// Animated custom-emoji (TGS/Lottie 60 FPS) community links
const COMMUNITY_LINKS = [
  { id: 'official', labelKey: 'officialChannel', label: 'Official Channel', url: 'https://t.me/+zK06d2Egp1dlYzQy', emojiId: 'Numeo8' },
  { id: 'kite_marketing', labelKey: 'kiteMarketing', label: 'Kite Marketing', url: MARKETING_CHANNEL, emojiId: '41' },
  { id: 'group_marketing', labelKey: 'groupMarketing', label: 'Group Marketing', url: 'https://t.me/+M2hTOpZEg09iZGMy', emojiId: '80' },
  { id: 'kite_info', labelKey: 'kiteInformation', label: 'Kite Information', url: 'https://t.me/app_kite_skins', emojiId: '87' },
  { id: 'contact', labelKey: 'contactTeamLink', label: 'Contact the Team', url: 'https://t.me/KiteSupportBot', emojiId: '92' },
];

export { MARKETING_CHANNEL };

interface ProfilePushProps {
  onNavigate: (page: string, data?: any) => void;
  onBack?: () => void;
  isActive?: boolean;
}

// ─── SVGS (100% MATCHING) ─── //

const SvgSettings = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g style={{ mixBlendMode: 'plus-lighter' }}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.14286 4C0.511674 4 0 4.51168 0 5.14286C0 5.77403 0.511674 6.28571 1.14286 6.28571H14.8571C15.4883 6.28571 16 5.77403 16 5.14286C16 4.51168 15.4883 4 14.8571 4H1.14286ZM1.14286 9.71429C0.511674 9.71429 0 10.226 0 10.8571C0 11.4883 0.511674 12 1.14286 12H14.8571C15.4883 12 16 11.4883 16 10.8571C16 10.226 15.4883 9.71429 14.8571 9.71429H1.14286Z"
        fill="white"
        fillOpacity={0.4}
        style={{ mixBlendMode: 'plus-lighter' }}
      />
    </g>
  </svg>
);

const SvgArrowRight = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      d="M8.71561 7.71422L5.13806 4.13667C4.96989 3.96849 4.8858 3.75445 4.8858 3.49454C4.8858 3.23463 4.96989 3.02059 5.13806 2.85242C5.30624 2.68424 5.52028 2.60015 5.78019 2.60015C6.04009 2.60015 6.25414 2.68424 6.42231 2.85242L10.642 7.07209C10.7337 7.16382 10.7987 7.2632 10.8369 7.37022C10.8751 7.47724 10.8942 7.59191 10.8942 7.71422C10.8942 7.83653 10.8751 7.95119 10.8369 8.05821C10.7987 8.16523 10.7337 8.26461 10.642 8.35634L6.42231 12.576C6.25414 12.7442 6.04009 12.8283 5.78019 12.8283C5.52028 12.8283 5.30624 12.7442 5.13806 12.576C4.96989 12.4078 4.8858 12.1938 4.8858 11.9339C4.8858 11.674 4.96989 11.4599 5.13806 11.2918L8.71561 7.71422Z"
      fill="#6D6D71"
    />
  </svg>
);

const SvgTerms = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="13" height="16" viewBox="0 0 13 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M3.42867 0C2.95529 0 2.57153 0.383755 2.57153 0.857143C2.57153 1.33053 2.95529 1.71429 3.42867 1.71429H10.8572C11.0151 1.71429 11.143 1.84221 11.143 2V12.8571C11.143 13.3305 11.5267 13.7143 12.0001 13.7143C12.4735 13.7143 12.8572 13.3305 12.8572 12.8571V2C12.8572 0.895431 11.9618 0 10.8572 0H3.42867ZM0 4.57143C0 3.62465 0.767509 2.85714 1.71429 2.85714H8C8.94678 2.85714 9.71429 3.62465 9.71429 4.57143V14.2857C9.71429 15.2325 8.94678 16 8 16H1.71429C0.767509 16 0 15.2325 0 14.2857V4.57143ZM1.85679 5.71429C1.85679 5.31979 2.17659 5 2.57107 5H7.1425C7.53699 5 7.85679 5.31979 7.85679 5.71429C7.85679 6.10878 7.53699 6.42857 7.1425 6.42857H2.57107C2.17659 6.42857 1.85679 6.10878 1.85679 5.71429Z"
      fill="currentColor"
    />
  </svg>
);

const SvgUs = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_us)">
      <circle cx="8" cy="8" r="8" fill="#F0F0F0" />
      {/* Red stripes */}
      <rect y="1.23" width="16" height="1.23" fill="#B22234" />
      <rect y="3.69" width="16" height="1.23" fill="#B22234" />
      <rect y="6.15" width="16" height="1.23" fill="#B22234" />
      <rect y="8.61" width="16" height="1.23" fill="#B22234" />
      <rect y="11.07" width="16" height="1.23" fill="#B22234" />
      <rect y="13.53" width="16" height="1.23" fill="#B22234" />
      {/* Blue canton */}
      <rect x="0" y="0" width="8" height="8.61" fill="#3C3B6E" />
      {/* Stars */}
      <circle cx="2" cy="2" r="0.6" fill="#FFFFFF" />
      <circle cx="4" cy="2" r="0.6" fill="#FFFFFF" />
      <circle cx="6" cy="2" r="0.6" fill="#FFFFFF" />
      <circle cx="3" cy="3.5" r="0.6" fill="#FFFFFF" />
      <circle cx="5" cy="3.5" r="0.6" fill="#FFFFFF" />
      <circle cx="2" cy="5" r="0.6" fill="#FFFFFF" />
      <circle cx="4" cy="5" r="0.6" fill="#FFFFFF" />
      <circle cx="6" cy="5" r="0.6" fill="#FFFFFF" />
      <circle cx="3" cy="6.5" r="0.6" fill="#FFFFFF" />
      <circle cx="5" cy="6.5" r="0.6" fill="#FFFFFF" />
    </g>
    <defs><clipPath id="clip_us"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgRu = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_ru)">
      <circle cx="8" cy="8" r="8" fill="#FFFFFF" />
      <rect y="5.33" width="16" height="5.34" fill="#0039A6" />
      <rect y="10.67" width="16" height="5.33" fill="#D52B1E" />
    </g>
    <defs><clipPath id="clip_ru"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgKo = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_ko)">
      <circle cx="8" cy="8" r="8" fill="#FFFFFF" />
      {/* Taegeuk Yin-Yang */}
      <path d="M4.6 8 A3.4 3.4 0 0 1 11.4 8 A1.7 1.7 0 0 0 8 8 A1.7 1.7 0 0 1 4.6 8 Z" fill="#CD2E3A" />
      <path d="M4.6 8 A3.4 3.4 0 0 0 11.4 8 A1.7 1.7 0 0 0 8 8 A1.7 1.7 0 0 1 4.6 8 Z" fill="#0047A0" />
      {/* 4 Trigrams */}
      {/* Top-left: Geon (3 solid) */}
      <g transform="translate(3.2, 3.2) rotate(-45)">
        <rect x="-1.8" y="-1.5" width="3.6" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="-0.5" width="3.6" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="0.5" width="3.6" height="0.6" rx="0.2" fill="#111" />
      </g>
      {/* Bottom-right: Gon (3 broken) */}
      <g transform="translate(12.8, 12.8) rotate(-45)">
        <rect x="-1.8" y="-1.5" width="1.5" height="0.6" rx="0.2" fill="#111" /><rect x="0.3" y="-1.5" width="1.5" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="-0.5" width="1.5" height="0.6" rx="0.2" fill="#111" /><rect x="0.3" y="-0.5" width="1.5" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="0.5" width="1.5" height="0.6" rx="0.2" fill="#111" /><rect x="0.3" y="0.5" width="1.5" height="0.6" rx="0.2" fill="#111" />
      </g>
      {/* Top-right: Gam (broken, solid, broken) */}
      <g transform="translate(12.8, 3.2) rotate(45)">
        <rect x="-1.8" y="-1.5" width="1.5" height="0.6" rx="0.2" fill="#111" /><rect x="0.3" y="-1.5" width="1.5" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="-0.5" width="3.6" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="0.5" width="1.5" height="0.6" rx="0.2" fill="#111" /><rect x="0.3" y="0.5" width="1.5" height="0.6" rx="0.2" fill="#111" />
      </g>
      {/* Bottom-left: Li (solid, broken, solid) */}
      <g transform="translate(3.2, 12.8) rotate(45)">
        <rect x="-1.8" y="-1.5" width="3.6" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="-0.5" width="1.5" height="0.6" rx="0.2" fill="#111" /><rect x="0.3" y="-0.5" width="1.5" height="0.6" rx="0.2" fill="#111" />
        <rect x="-1.8" y="0.5" width="3.6" height="0.6" rx="0.2" fill="#111" />
      </g>
    </g>
    <defs><clipPath id="clip_ko"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgZh = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_zh)">
      <circle cx="8" cy="8" r="8" fill="#DE2910" />
      {/* Big star */}
      <polygon points="4.5,2.4 5.1,4.2 7,4.2 5.5,5.3 6,7.1 4.5,6 3,7.1 3.5,5.3 2,4.2 3.9,4.2" fill="#FFDE00" />
      {/* 4 Small stars */}
      <polygon points="8.2,2 8.5,2.7 9.2,2.7 8.6,3.1 8.8,3.8 8.2,3.4 7.6,3.8 7.8,3.1 7.2,2.7 7.9,2.7" fill="#FFDE00" transform="rotate(15 8.2 2.9)" />
      <polygon points="9.8,3.8 10.1,4.5 10.8,4.5 10.2,4.9 10.4,5.6 9.8,5.2 9.2,5.6 9.4,4.9 8.8,4.5 9.5,4.5" fill="#FFDE00" transform="rotate(30 9.8 4.7)" />
      <polygon points="9.8,6.4 10.1,7.1 10.8,7.1 10.2,7.5 10.4,8.2 9.8,7.8 9.2,8.2 9.4,7.5 8.8,7.1 9.5,7.1" fill="#FFDE00" />
      <polygon points="8.2,8.2 8.5,8.9 9.2,8.9 8.6,9.3 8.8,10 8.2,9.6 7.6,10 7.8,9.3 7.2,8.9 7.9,8.9" fill="#FFDE00" transform="rotate(-20 8.2 9.1)" />
    </g>
    <defs><clipPath id="clip_zh"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgUa = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_ua)">
      <circle cx="8" cy="8" r="8" fill="#FFD700" />
      <rect x="0" y="0" width="16" height="8" fill="#0057B7" />
    </g>
    <defs><clipPath id="clip_ua"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgFa = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_fa)">
      <circle cx="8" cy="8" r="8" fill="#FFFFFF" />
      <rect x="0" y="0" width="16" height="5.33" fill="#239F40" />
      <rect x="0" y="10.67" width="16" height="5.33" fill="#DA0000" />
      {/* Emblem of Iran in center */}
      <path d="M8 6.5C7.4 7.2 7 8 7 8.8C7.5 8.4 8 8.4 8 8.4C8 8.4 8.5 8.4 9 8.8C9 8 8.6 7.2 8 6.5Z" fill="#DA0000" />
      <path d="M6 7.5C5.5 8.2 5.5 8.9 6 9.5C6.3 9.2 6.6 8.7 6.6 8.2C6.6 7.9 6.4 7.7 6 7.5Z" fill="#DA0000" />
      <path d="M10 7.5C9.6 7.7 9.4 7.9 9.4 8.2C9.4 8.7 9.7 9.2 10 9.5C10.5 8.9 10.5 8.2 10 7.5Z" fill="#DA0000" />
      <line x1="8" y1="6.2" x2="8" y2="9.8" stroke="#DA0000" strokeWidth="0.8" strokeLinecap="round" />
    </g>
    <defs><clipPath id="clip_fa"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgAr = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_ar)">
      <circle cx="8" cy="8" r="8" fill="#007A3D" />
      {/* Calligraphy styling */}
      <path d="M3.2 6.2H4.4V7.6H3.2V6.2ZM4.8 5.6H5.8V7.6H4.8V5.6ZM6.4 5H7.4V7.6H6.4V5ZM8 5.4H9V7.6H8V5.4ZM9.6 5.2H10.6V7.6H9.6V5.2ZM11.2 5.8H12.4V7.6H11.2V5.8Z" fill="#FFFFFF" />
      <path d="M4 7.6C5.5 7.8 10.5 7.8 12 7.6" stroke="#FFFFFF" strokeWidth="0.7" strokeLinecap="round" />
      {/* Saudi Sword */}
      <line x1="4.2" y1="9.8" x2="11.8" y2="9.8" stroke="#FFFFFF" strokeWidth="1" strokeLinecap="round" />
      <line x1="10.8" y1="8.8" x2="10.8" y2="10.8" stroke="#FFFFFF" strokeWidth="0.9" strokeLinecap="round" />
      <circle cx="11.8" cy="9.8" r="0.5" fill="#FFFFFF" />
    </g>
    <defs><clipPath id="clip_ar"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const SvgTr = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <g clipPath="url(#clip_tr)">
      <circle cx="8" cy="8" r="8" fill="#E30A17" />
      <circle cx="6.5" cy="8" r="3.6" fill="#FFFFFF" />
      <circle cx="7.3" cy="8" r="2.9" fill="#E30A17" />
      <polygon points="10.2,6.9 10.7,7.8 11.7,7.4 11.2,8.4 11.9,9.2 10.9,9 10.4,10 10.1,9 9.1,8.7 10,8.1" fill="#FFFFFF" />
    </g>
    <defs><clipPath id="clip_tr"><circle cx="8" cy="8" r="8" /></clipPath></defs>
  </svg>
);

const LANGUAGES_CONFIG = [
  { code: 'ar', label: 'AR', Icon: SvgAr },
  { code: 'en', label: 'EN', Icon: SvgUs },
  { code: 'tr', label: 'TR', Icon: SvgTr },
  { code: 'ru', label: 'RU', Icon: SvgRu },
  { code: 'ko', label: 'KO', Icon: SvgKo },
  { code: 'zh-CN', label: 'ZH', Icon: SvgZh },
  { code: 'uk', label: 'UA', Icon: SvgUa },
  { code: 'fa', label: 'FA', Icon: SvgFa },
];

const CustomEmoji104Player: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const animInstance = React.useRef<any>(null);

  React.useEffect(() => {
    let isMounted = true;

    async function loadLottie(): Promise<any> {
      if ((window as any).lottie) return (window as any).lottie;
      return new Promise((resolve, reject) => {
        const existing = document.getElementById('lottie-script');
        if (existing) {
          existing.addEventListener('load', () => resolve((window as any).lottie));
          return;
        }
        const script = document.createElement('script');
        script.id = 'lottie-script';
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.12.2/lottie.min.js';
        script.onload = () => resolve((window as any).lottie);
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }

    async function init() {
      const urls = [
        '/emojis/5967522716062847679.json',
        '/emojis/5987802868734760945.json'
      ];
      let animationData: any = null;

      for (const u of urls) {
        try {
          const res = await fetch(u);
          if (res.ok) {
            const text = await res.text();
            if (text.startsWith('{')) {
              animationData = JSON.parse(text);
              break;
            }
          }
        } catch (_) {}
      }

      if (!animationData) return;

      try {
        const lottieObj = await loadLottie();
        if (!isMounted || !containerRef.current || !lottieObj) return;
        if (animInstance.current) animInstance.current.destroy();
        animInstance.current = lottieObj.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData
        });
      } catch (err) {
        console.warn('[Emoji104] Error initializing lottie:', err);
      }
    }

    init();

    return () => {
      isMounted = false;
      if (animInstance.current) animInstance.current.destroy();
    };
  }, []);

  return (
    <div className="absolute -bottom-2 -right-2 z-20 w-8 h-8 flex items-center justify-center drop-shadow-md pointer-events-none">
      <div ref={containerRef} className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:block" />
    </div>
  );
};

export const ProfilePush: React.FC<ProfilePushProps> = ({ onNavigate }) => {
  const { user, wallet } = useStore();
  const { impactOccurred, selectionChanged } = useHapticFeedback();
  const { t, i18n } = useTranslation(['profile', 'nav', 'common', 'store', 'marketplace', 'username']);
  
  // Initialize stats locally with zero loading delay
  const [stats, setStats] = useState<any>({
    total_nfts: 0,
    inventory_cost: 0,
    bought_sold: '5/0',
    total_volume: '57.1'
  });

  // V2 stats / rating / summary
  const [v2Stats, setV2Stats] = useState<any>(null);
  const [v2Rating, setV2Rating] = useState<any>(null);
  const [v2UserSummary, setV2UserSummary] = useState<any>(null);

  // Settings Modal State
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState<string>(i18n.language || 'en');
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [highValueOffersOnly, setHighValueOffersOnly] = useState(false);
  const [tactileFeedback, setTactileFeedback] = useState(true);
  const [animatedGifts, setAnimatedGifts] = useState(true);

  useEffect(() => {
    if (i18n.language) {
      setSelectedLang(i18n.language);
    }
  }, [i18n.language]);

  useEffect(() => {
    const handleOpenSettings = () => setIsSettingsOpen(true);
    window.addEventListener('open_settings', handleOpenSettings);
    return () => window.removeEventListener('open_settings', handleOpenSettings);
  }, []);

  useEffect(() => {
    if (!user?.telegram_id && !user?.id) return;
    const uid = (user?.telegram_id || user?.id) as any;
    v2Api.stats(uid).then((d: any) => {
      if (d?.success && d.stats) {
        setStats(d.stats);
        setV2Stats(d.stats);
      }
    }).catch(() => {});
    v2Api.rating(uid).then((d: any) => { if (d?.success) setV2Rating(d.rating); }).catch(() => {});
    v2Api.summary(uid).then((d: any) => { if (d?.success) setV2UserSummary(d.summary || d.user); }).catch(() => {});
  }, [user?.telegram_id, user?.id]);

  const level = Number(v2UserSummary?.level ?? user?.level ?? 1);
  const xpProgress = Math.min(100, Math.max(0, Number(user?.xp_progress) || 0));
  const currentXp = Math.floor(Number(user?.current_xp) || 0);
  const isDev = String(user?.telegram_id) === '601287447';
  const verification = String(v2UserSummary?.verification_status || (user as any)?.verification_status || 'unverified').replace('_', ' ');
  const rating = v2UserSummary?.rating != null
    ? Number(v2UserSummary.rating).toFixed(1)
    : (v2Rating?.average_rating != null ? Number(v2Rating.average_rating).toFixed(1) : '—');
  const role = String(v2UserSummary?.role || user?.role || 'user').toUpperCase();

  const handleCopyLink = () => {
    impactOccurred('light');
    const shareText = `Join me on App!`;
    const link = `https://t.me/share/url?url=${encodeURIComponent(window.location.origin)}&text=${encodeURIComponent(shareText)}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).catch(() => {});
    }
  };

  const formattedBalance =
    wallet?.available_balance !== undefined
      ? Number(wallet.available_balance).toLocaleString('en-US', { maximumFractionDigits: 1 })
      : '1,119.9...';

  const displayName = user?.first_name
    ? user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.first_name
    : user?.username || 'Mohammed Qasim';

  return (
    <div className="min-h-screen text-white px-4 pt-4 pb-36 select-none font-sans max-w-md mx-auto relative">

      {/* ─── 1. TOP BAR HEADER (Glass BalanceBar with SvgSettings) ─── */}
      <div className="mb-3 pt-1">
        <BalanceBar
          onDepositClick={() => onNavigate('wallet', 'deposit')}
          onHistoryClick={() => setIsSettingsOpen(true)}
          logoVariant="kite"
          logoIcon={<SvgSettings className="w-4 h-4 text-white" />}
        />
      </div>

      {/* ─── 2. PROFILE HERO CARD (Glass, transferred from Full Profile) ─── */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.03, type: 'spring', stiffness: 240, damping: 26 }}
        className="relative z-10 mb-3"
      >
        <div className="glass rounded-[30px] p-5 relative overflow-hidden">
          <div className="pointer-events-none absolute top-[-50%] right-[-20%] w-[70%] h-[80%] bg-gradient-to-br from-indigo-500/20 to-purple-500/5 blur-[60px] rounded-full" />
          <div className="glass-sweep rounded-[30px]" />

          <div className="relative flex items-center gap-4">
            {/* Avatar + Level pill */}
            <div className="relative shrink-0">
              <div className="absolute inset-0 bg-amber-400/20 blur-2xl rounded-full scale-150 pointer-events-none" />
              <div className="glass-inset w-20 h-20 rounded-[24px] p-[3px] relative z-10">
                <div className="w-full h-full rounded-[20px] overflow-hidden">
                  <img
                    src={user?.photo_url || '/icons/default-avatar.svg'}
                    alt="avatar"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = '/icons/default-avatar.svg'; }}
                  />
                </div>
              </div>
              <CustomEmoji104Player />
            </div>

            {/* Identity */}
            <div className="flex-1 min-w-0">
              <h1 className="text-[20px] font-black text-white tracking-tighter truncate flex items-center gap-2">
                {user?.first_name} {user?.last_name}
                {isDev && (
                  <span className="bg-gradient-to-r from-[#B4F73D]/20 to-teal-400/20 text-[#B4F73D] border border-[#B4F73D]/30 px-1.5 py-0.5 rounded-md text-[9px] uppercase font-black tracking-widest shrink-0">
                    Developer
                  </span>
                )}
              </h1>
              <div className="flex items-center flex-wrap gap-1.5 mt-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-black text-white/70 bg-white/[0.06] border border-white/10 px-2 py-1 rounded-lg">
                  <AtSign size={10} className="text-cyan-300" /> {user?.username || 'user'}
                </span>
                <span className="text-[9px] px-2 py-1 rounded-lg bg-white/[0.06] border border-white/10 text-white/40 font-mono font-bold">
                  ID {user?.telegram_id}
                </span>
                {String(verification).toLowerCase() === 'verified' && (
                  <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-400/25 px-2 py-1 rounded-lg">
                    <BadgeCheck size={10} /> Verified
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* 4 Market Activity Counters row (Interactive & linked to Full Profile tabs) */}
          <div className="relative grid grid-cols-4 gap-2 mt-5">
            {([
              { icon: <ShoppingBag size={13} className="text-blue-300" />, label: 'Sales', val: v2Stats?.total_sales ?? 0, route: 'full_profile' },
              { icon: <Handshake size={13} className="text-purple-300" />, label: 'Purchases', val: v2Stats?.total_purchases ?? 0, route: 'full_profile' },
              { icon: <Tag size={13} className="text-cyan-300" />, label: 'Listings', val: v2Stats ? `${(v2Stats.active_listings || 0)}/${(v2Stats.sold_listings || 0)}` : '0/0', route: 'full_profile' },
              { icon: <Gavel size={13} className="text-orange-300" />, label: 'Won', val: v2Stats?.auctions_won ?? 0, route: 'full_profile' },
            ]).map((t, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  selectionChanged();
                  onNavigate(t.route);
                }}
                className="glass-inset hover:bg-white/[0.12] active:scale-[0.95] transition-all rounded-2xl px-1 py-3 flex flex-col items-center gap-1 text-center cursor-pointer border border-transparent hover:border-white/20"
              >
                <span className="text-white/60">{t.icon}</span>
                <span className="text-[13px] font-black text-white tracking-tight leading-none">
                  {t.val}
                </span>
                <span className="text-[7px] font-bold text-white/35 uppercase tracking-widest">{t.label}</span>
              </button>
            ))}
          </div>

          {/* XP progress */}
          <div className="relative mt-4">
            <div className="flex justify-between text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">
              <span className="flex items-center gap-1">
                <Sparkles size={11} className="text-amber-300" /> Level {level} · {currentXp.toLocaleString()} XP
              </span>
              <span className="text-white/70">{Math.round(xpProgress)}%</span>
            </div>
            <div className="h-2.5 bg-black/40 rounded-full overflow-hidden border border-white/10">
              <div
                className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-full shadow-[0_0_15px_rgba(99,102,241,0.7)]"
                style={{ width: `${xpProgress}%` }}
              />
            </div>
          </div>
        </div>
      </motion.div>



      {/* ─── 5. FULL PROFILE CARD (GLASS) ─── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
      >
        <button
          onClick={() => {
            selectionChanged();
            onNavigate('full_profile');
          }}
          className="w-full glass hover:bg-white/[0.1] active:scale-[0.99] transition-all rounded-[28px] p-4 border border-white/10 shadow-xl flex items-center justify-between mb-4 group text-left cursor-pointer relative overflow-hidden"
        >
          <div className="glass-sweep rounded-[28px]" />
          <div className="flex items-center gap-3.5 relative z-10">
            <div className="w-11 h-11 rounded-2xl glass-strong bg-blue-500/20 border border-blue-400/30 flex items-center justify-center text-blue-300 group-hover:scale-105 transition-transform shrink-0 shadow-md">
              <UserCheck className="w-5 h-5 text-blue-300" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white group-hover:text-blue-300 transition-colors">{t('profile:fullProfilePage')}</h3>
              <p className="text-xs text-white/50 mt-0.5">{t('profile:fullProfileSubtitle')}</p>
            </div>
          </div>

          <div className="w-8 h-8 rounded-full glass-chip flex items-center justify-center text-white/50 group-hover:bg-blue-500 group-hover:text-white transition-all shrink-0 ml-1 relative z-10">
            <ChevronRight className="w-4 h-4" />
          </div>
        </button>
      </motion.div>

      {/* ─── 6. REFERRAL SECTION TITLE ─── */}
      <div className="mt-2">
        <h3 className="text-[11px] font-bold tracking-wider text-white/40 uppercase mb-2.5 px-1 flex items-center gap-1">
          <span>{t('profile:inviteTitle')}</span>
          <span className="text-xs">💎</span>
        </h3>

        {/* ─── 7. REFERRAL CARD (GLASS) ─── */}
        <div className="glass rounded-[28px] p-4 border border-white/10 mb-4 flex items-start gap-3.5 shadow-xl relative overflow-hidden">
          <div className="glass-sweep rounded-[28px]" />
          <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 flex items-center justify-center shrink-0 shadow-md mt-0.5 relative z-10">
            <Percent className="w-4.5 h-4.5 text-emerald-300 stroke-[2.5]" />
          </div>
          <div className="relative z-10">
            <h4 className="text-base font-bold text-white mb-0.5">{t('profile:referralPaused')}</h4>
            <p className="text-xs text-white/50 leading-snug">{t('profile:referralPausedHint')}</p>
          </div>
        </div>

        {/* ─── 8. INVITE BUTTON (GLASS GRADIENT) ─── */}
        <button
          onClick={handleCopyLink}
          className="w-full py-4 rounded-full glass-strong bg-gradient-to-r from-blue-600/90 via-indigo-600/90 to-cyan-600/90 hover:from-blue-500 hover:to-cyan-500 active:scale-[0.98] transition-all text-white font-black text-base shadow-[0_8px_25px_rgba(0,122,255,0.4)] border border-white/25 flex items-center justify-center gap-2 mb-4 cursor-pointer relative overflow-hidden"
        >
          <div className="glass-sweep rounded-full" />
          <span className="relative z-10">{t('profile:inviteFriends')}</span>
        </button>
      </div>

      {/* ─── 9. POPUP SETTINGS MODAL (100% ENGLISH) ─── */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isSettingsOpen && (
            <div className="fixed inset-0 z-[100] flex items-end justify-center">

            {/* Drawer Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => {
                impactOccurred('light');
                setIsSettingsOpen(false);
              }}
              className="fixed inset-0 backdrop-blur-[16px] bg-black/30 z-[100]"
            />

            {/* Drawer Content */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 34 }}
              className="fixed right-0 bottom-0 left-0 z-[101] mx-auto flex max-w-[512px] flex-col outline-none w-full rounded-t-[24px] bg-[#1c1c1e] border-t border-x border-white/10 p-4 shadow-2xl max-h-[calc(100vh-48px)] overflow-y-auto text-left"
            >

              {/* Drawer Top Bar Handle */}
              <div className="w-9 h-1 bg-white/20 rounded-full mx-auto mb-3" />

              {/* Drawer Title Header */}
              <div className="flex items-center justify-between pb-2 mb-3 border-b border-white/[0.06]">
                <h3 className="text-base font-bold text-white tracking-tight">{t('nav:menu')}</h3>
                <button
                  onClick={() => setIsSettingsOpen(false)}
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Drawer Inner Elements Structure */}
              <div className="flex flex-col gap-3">

                {/* 1. LANGUAGE SECTION */}
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-semibold text-[#8E8E93] uppercase">{t('common:language', 'Language')}</div>
                  <div className="flex gap-2 flex-wrap">
                    {LANGUAGES_CONFIG.map(({ code, label, Icon }) => {
                      const isActive =
                        selectedLang === code ||
                        (code === 'zh-CN' && selectedLang.startsWith('zh')) ||
                        (code === 'uk' && (selectedLang === 'uk' || selectedLang === 'ua'));
                      return (
                        <button
                          key={code}
                          onClick={async () => {
                            impactOccurred('light');
                            setSelectedLang(code);
                            await i18n.changeLanguage(code);
                            setAppLanguage(code as LanguageCode);
                          }}
                          className={`py-[10px] px-3.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all cursor-pointer ${
                            isActive
                              ? 'bg-white text-black font-bold shadow-sm'
                              : 'bg-[#252528] text-[#8E8E93] hover:text-white'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="h-px bg-[#252528]" />

                {/* 2. PREFERENCES SECTION */}
                <div className="bg-[#252528] rounded-2xl p-3.5 flex flex-col gap-3">
                  <div className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">{t('profile:preferences')}</div>

                  {/* NOTIFICATIONS ROW */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-white">{t('nav:notifications')}</span>
                      <span className="text-xs text-[#8E8E93]">{t('profile:pushAlerts')}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        impactOccurred('light');
                        setNotificationsEnabled(!notificationsEnabled);
                      }}
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out p-0.5 outline-none ${
                        notificationsEnabled ? 'bg-[#007aff]' : 'bg-[#3A3A3C]'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow-md transform ring-0 transition duration-200 ease-in-out ${
                          notificationsEnabled ? 'translate-x-[20px]' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {notificationsEnabled && (
                    <label className="flex items-center gap-2.5 text-xs text-[#8E8E93] hover:text-white cursor-pointer pl-0.5 transition-colors">
                      <input
                        type="checkbox"
                        checked={highValueOffersOnly}
                        onChange={(e) => {
                          impactOccurred('light');
                          setHighValueOffersOnly(e.target.checked);
                        }}
                        className="w-4 h-4 rounded border-white/20 accent-[#007aff] cursor-pointer"
                      />
                      <span>{t('profile:highValueOnly')}</span>
                    </label>
                  )}

                  <div className="h-px bg-white/5" />

                  {/* TACTILE RESPONSE ROW */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-white">{t('profile:tactileResponse')}</span>
                      <span className="text-xs text-[#8E8E93]">{t('profile:hapticHint')}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        impactOccurred('light');
                        setTactileFeedback(!tactileFeedback);
                      }}
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out p-0.5 outline-none ${
                        tactileFeedback ? 'bg-[#007aff]' : 'bg-[#3A3A3C]'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow-md transform ring-0 transition duration-200 ease-in-out ${
                          tactileFeedback ? 'translate-x-[20px]' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="h-px bg-white/5" />

                  {/* ANIMATED GIFTS ROW */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-white">{t('profile:animatedGifts')}</span>
                      <span className="text-xs text-[#8E8E93]">{t('profile:stickerAnimations')}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        impactOccurred('light');
                        setAnimatedGifts(!animatedGifts);
                      }}
                      className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 ease-in-out p-0.5 outline-none ${
                        animatedGifts ? 'bg-[#007aff]' : 'bg-[#3A3A3C]'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-6 w-6 rounded-full bg-white shadow-md transform ring-0 transition duration-200 ease-in-out ${
                          animatedGifts ? 'translate-x-[20px]' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* 5. COMMUNITY LINKS (animated custom emoji) */}
                <div className="bg-[#252528] rounded-2xl py-2 px-3">
                  <div className="flex flex-col gap-2">
                    {COMMUNITY_LINKS.map((link, idx) => (
                      <React.Fragment key={link.id}>
                        {idx > 0 && <div className="h-px bg-white/5" />}
                        <button
                          type="button"
                          onClick={() => {
                            impactOccurred('light');
                            openTelegram(link.url);
                          }}
                          className="relative flex items-center justify-between gap-2 text-sm text-[#8E8E93] hover:text-white transition-colors w-full text-left cursor-pointer py-0.5"
                        >
                          <div className="flex gap-2.5 items-center min-w-0">
                            <CustomEmojiPlayer emojiId={link.emojiId} size={22} />
                            <span className="text-white font-semibold text-sm truncate">
                              {t(`profile:${link.labelKey}`, link.label)}
                            </span>
                          </div>
                          <SvgArrowRight className="w-3.5 h-3.5 shrink-0" />
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                {/* 6. CONTACT THE TEAM BUTTON */}
                <button
                  onClick={() => {
                    setIsSettingsOpen(false);
                    onNavigate('chat');
                  }}
                  className="w-full py-3.5 rounded-full bg-[#007aff] hover:bg-[#0066cc] active:scale-[0.98] transition-all text-white font-bold text-sm shadow-md cursor-pointer"
                >{t('profile:contactTeam')}</button>

                {/* 7. TERMS & PRIVACY */}
                <div className="flex items-center justify-center gap-4 py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsSettingsOpen(false);
                      onNavigate('terms');
                    }}
                    className="flex items-center justify-center gap-1.5 text-[12px] font-semibold text-[#8E8E93] hover:text-white transition-colors cursor-pointer"
                  >
                    <SvgTerms className="w-3.5 h-3.5" />
                    <span>{t('profile:termsOfService')}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIsSettingsOpen(false);
                      onNavigate('privacy');
                    }}
                    className="flex items-center justify-center gap-1.5 text-[12px] font-semibold text-[#8E8E93] hover:text-white transition-colors cursor-pointer"
                  >
                    <SvgTerms className="w-3.5 h-3.5" />
                    <span>{t('profile:privacyPolicy')}</span>
                  </button>
                </div>

              </div>
            </motion.div>
          </div>
          )}
        </AnimatePresence>,
        document.body
      )}

    </div>
  );
};

export default ProfilePush;
